import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { roundToPrecision, slopeEstimationPercentile, interceptEstimationPercentile } from '@cf-shared/shared-util';
import { Chart, ChartConfiguration, ChartData, registerables } from 'chart.js';
import { color as chartColor } from 'chart.js/helpers';
import { straightTunnelForShownDays } from './trend-chart-tunnel';

import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
export interface TrendChartDataset {
  name: string;
  values: number[];
  color: string;
}

@Component({
  selector: 'cs-trend-chart',
  templateUrl: 'cs-trend-chart.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTrendChartComponent {
  protected LL = LL;

  @Input() title: TranslatedString;
  @Input() unit: TranslatedString = untranslatedString('');
  @Input() datasets: TrendChartDataset[] = [];
  @Input() days: string[] = [];
  @Input() loading: boolean = false;
  @Input() chartType: 'line' | 'bar' = 'line';
  @Input() tooltipInfo?: TranslatedString;
  @Input() positiveIsGood: boolean = true; // true = upward trend is good (bulking), false = downward trend is good (cutting)
  /** draw the straight 30/70 tunnel around the trend line, in one tint (#6627, the RunningTrendCharts switch) */
  @Input() showTunnel: boolean = false;
  @Output() seeAllClick: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('chartCanvas', { static: true }) canvasElement: ElementRef;
  @ViewChild('canvasHolder', { static: true }) canvasHolderElement: ElementRef;

  chart: Chart;
  protected currentValue: number = undefined;
  protected datasetsGains: number[] = [];
  protected isEmpty = false;

  constructor(
    protected elementRef: ElementRef,
    protected cd: ChangeDetectorRef,
  ) {
    // Initialization logic if needed

    Chart.register(...registerables);
  }

  resizeObserver = new ResizeObserver(() => void this.onResize());

  async onResize() {
    void this.updateChart({ fromResize: true });
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }

  ngAfterViewInit(): void {
    this.resizeObserver.observe(document.body);
    this.resizeObserver.observe(this.elementRef.nativeElement);
    const element = this.elementRef.nativeElement as HTMLElement;
    element.removeAttribute('title');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.title || changes.unit || changes.datasets || changes.days || changes.showTunnel) {
      void this.updateChart()
    }

    if (changes.loading) {
      let chartElement = this.canvasHolderElement.nativeElement as HTMLCanvasElement;
      chartElement.hidden = this.loading;
      this.cd.markForCheck();
    }
  }

  private calculateTheilSenTrendLine(values: number[]): { startValue: number; endValue: number; currentValue: number } | null {
    // Collect points with valid values (skip undefined)
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i < values.length; i++) {
      if (values[i] !== undefined && values[i] !== null && !isNaN(values[i])) {
        // Use negative days from end (similar to plan-update.ts)
        const daysFromEnd = i - (values.length - 1);
        points.push({ x: daysFromEnd, y: values[i] });
      }
    }

    // Need at least 2 points to calculate a trend line
    if (points.length < 2) {
      return null;
    }

    // Calculate Theil-Sen slope and intercept (same as plan-update.ts)
    const slope = slopeEstimationPercentile(points, 0.5);
    const intercept = interceptEstimationPercentile(points, slope, 0.5);

    // Calculate start point (day 0, which is -(length-1) days from end)
    const startValue = roundToPrecision(slope * (-(values.length - 1)) + intercept, 0.1);
    // Calculate end point (last day, which is 0 days from end)
    const endValue = roundToPrecision(slope * 0 + intercept, 0.1);
    // Calculate current value as weight 7 days ago (matching plan-update.ts: weightSlopeM * -7 + weightInterceptM)
    const currentValue = roundToPrecision(slope * -7 + intercept, 0.1);

    return { startValue, endValue, currentValue };
  }

  createLineChart() {
    // Display only last 7 days if we have more than 7 days of data
    const displayLabels = this.days.length > 7 ? this.days.slice(-7) : this.days;

    const data: ChartData = {
      labels: displayLabels,
      datasets: [],
    };

    Chart.defaults.color = "#fff";
    const verticalLinePlugin = {
      id: 'verticalLine',
      afterDraw: (chart) => {
        if (chart.tooltip?.getActiveElements()?.length) {
          let activePoint = chart.tooltip.getActiveElements()[0];
          let ctx = chart.ctx;
          let x = activePoint.element.x;
          let topY = chart.chartArea.top;
          let bottomY = chart.chartArea.bottom;

          // Set line opts
          ctx.save();
          ctx.lineWidth = 1.5;
          ctx.setLineDash([7, 5]);
          ctx.strokeStyle = 'gray';

          // draw vertical line
          ctx.beginPath();
          ctx.moveTo(x, topY);
          ctx.lineTo(x, bottomY);
          ctx.stroke();
          ctx.restore();
        }
      }
    };

    // Register the plugin
    Chart.register(verticalLinePlugin);

    let yUnits: { [key: string]: string } = {};
    for (const dataset of this.datasets) {
      yUnits[dataset.name] = this.unit;
    }

    const config: ChartConfiguration = {
      type: 'line',
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false, // critical
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
        plugins: {
          tooltip: {
            intersect: false,
            mode: 'nearest',
            filter: (tooltipItem) => {
              // Filter out trend line datasets (they have pointRadius = 0)
              return tooltipItem.dataset.pointRadius !== 0;
            },
            callbacks: {
              label: (context) => {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                label += roundToPrecision(context.parsed.y, 0.1);
                label += ' ' + yUnits[context.dataset.yAxisID];
                return label;
              },
            },
            displayColors: false,
          },
          legend: {
            display: false,
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'day',
              minUnit: 'day',
              tooltipFormat: 'yyyy-MM-dd',
            },
            ticks: {
              display: false  // Hides X-axis labels
            },
            grid: {
              drawOnChartArea: false,
              display: false,
              drawTicks: false,
            },
            border: {
              display: false, // Hides the border of the X-axis
            }
          },
          y: {
            ticks: {
              display: false, // Hides Y-axis labels
            },
            grid: {
              drawOnChartArea: false,
              display: false,
              drawTicks: false,
            },
            border: {
              display: false, // Hides the border of the X-axis
            }
          }
        },
      },
    };

    for (const dataset of this.datasets) {
      // Display only last 7 points if we have more than 7 days
      const displayValues = dataset.values.length > 7 ? dataset.values.slice(-7) : dataset.values;

      data.datasets.push({
        yAxisID: dataset.name,
        fill: false,
        pointRadius: 4,
        hoverRadius: 5,
        hoverBorderWidth: 2,
        pointStyle: 'circle',
        pointBackgroundColor: '#313133',
        pointBorderColor: dataset.color,
        pointBorderWidth: 2,
        borderColor: dataset.color,
        borderDash: undefined,
        showLine: false, // Don't show connecting lines, only show points
        data: displayValues,
      });

      // Calculate Theil-Sen trend line using ALL data (for 14-day trend)
      const trendLine = this.calculateTheilSenTrendLine(dataset.values);
      if (trendLine) {
        // Create trend line data for the displayed period
        const trendLineData = new Array(displayValues.length).fill(undefined);

        // If we have more than 7 days, adjust the trend line to show only the last 7 days portion
        if (dataset.values.length > 7) {
          // Calculate the slope from the full dataset trend
          const fullSlope = (trendLine.endValue - trendLine.startValue) / (dataset.values.length - 1);
          // Start point for the 7-day window (7 days from the end)
          const startDayOffset = dataset.values.length - 7;
          trendLineData[0] = trendLine.startValue + (fullSlope * startDayOffset);
          trendLineData[trendLineData.length - 1] = trendLine.endValue;
        } else {
          // Use the full trend line if we have 7 or fewer days
          trendLineData[0] = trendLine.startValue;
          trendLineData[trendLineData.length - 1] = trendLine.endValue;
        }

        // Add trend line as a separate dataset
        data.datasets.push({
          yAxisID: dataset.name,
          fill: false,
          pointRadius: 0, // No points for trend line
          pointHoverRadius: 0,
          pointHitRadius: 0, // Prevent hover detection
          borderColor: dataset.color,
          borderDash: [5, 5], // Dashed line for trend
          borderWidth: 2,
          data: trendLineData,
          spanGaps: true, // This will connect the first and last points
          tension: 0, // Straight line
        });
      }

      // the straight 30/70 tunnel around the line (#6627): one tint, no border, read at the shown days only
      const tunnel = this.showTunnel ? straightTunnelForShownDays(dataset.values, displayValues.length) : undefined;
      if (tunnel) {
        const edge = (pick: (end: { low: number; high: number }) => number) => {
          const edgeData = new Array(displayValues.length).fill(undefined);
          edgeData[0] = pick(tunnel.start);
          edgeData[edgeData.length - 1] = pick(tunnel.end);
          return edgeData;
        };
        const tunnelEdge = { yAxisID: dataset.name, pointRadius: 0, pointHoverRadius: 0, pointHitRadius: 0, borderWidth: 0, borderColor: 'transparent', spanGaps: true, tension: 0 };
        // the upper edge is filled down to the lower edge that follows it
        data.datasets.push({ ...tunnelEdge, data: edge(end => end.high), fill: '+1', backgroundColor: chartColor(dataset.color).alpha(0.2).rgbString() });
        data.datasets.push({ ...tunnelEdge, data: edge(end => end.low), fill: false });
      }

      config.options.scales[dataset.name] = {
        display: true,
        type: 'linear',
        title: {
          display: false,
        },
        ticks: {
          precision: 1,
          display: false,
        },
        grid: {
          drawOnChartArea: false,
          display: false,
          drawTicks: false,
        },
        border: {
          display: false, // Hides the border of the Y-axis
        }
      }
    }

    return config;
  }

  createBarChart() {
    const labels = this.days;

    const data: ChartData = {
      labels: labels,
      datasets: [],
    };

    const config = {
      type: 'bar',
      data: data,
      options: {
        responsive: true,
        maintainAspectRatio: false, // critical
        plugins: {
          legend: {
            position: 'top',
            display: false,
          },
          title: {
            display: false,
          },
          tooltip: {
            intersect: false,
            mode: 'index',
            callbacks: {
              label: (context) => {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                // Always show absolute (positive) value
                const value = Math.abs(context.parsed.y);
                label += roundToPrecision(value, 0.1);
                if (this.unit) {
                  label += ' ' + this.unit;
                }
                return label;
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            type: 'category',
            time: {
              unit: 'day',
              minUnit: 'day',
              tooltipFormat: 'yyyy-MM-dd',
            },
            ticks: {
              display: false  // Hides X-axis labels
            },
            grid: {
              drawOnChartArea: false,
              display: false,
              drawTicks: false,
            },
            border: {
              display: false, // Hides the border of the X-axis
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              display: false, // Hides Y-axis labels
            },
            grid: {
              drawOnChartArea: false,
              display: false,
              drawTicks: false,
            },
            border: {
              display: false, // Hides the border of the X-axis
            },
          }
        },
      },
    };

    for (const dataset of this.datasets) {
      data.datasets.push({
        fill: true,
        backgroundColor: dataset.color,
        data: dataset.values,
        minBarLength: 0,
        borderRadius: 5,
        stack: 'combined`'
      });
    }

    return config;
  }

  async updateChart({ fromResize }: { fromResize?: boolean } = {}) {

    this.datasetsGains = [];

    let config: any;
    if (this.chartType === 'line') {
      config = this.createLineChart();
    } else if (this.chartType === 'bar') {
      config = this.createBarChart();
    }

    if (this.chart) {
      this.chart.destroy();
    }
    const chartElement = this.canvasElement.nativeElement as HTMLCanvasElement;
    this.chart = new Chart(chartElement, config);

    const canvasHolderElement = this.canvasHolderElement.nativeElement as HTMLElement;
    this.isEmpty = this.datasets.length === 0 || !this.datasets.some(dataset => dataset.values.some(x => !isNaN(x)));
    if (this.isEmpty) {
      // no data — keep canvas visible with watermark
      chartElement.style.removeProperty('display');
      canvasHolderElement.style.removeProperty('display');
    } else {
      canvasHolderElement.style.removeProperty('display');
    }
    // only trigger CD when data changed (ngOnChanges), not on resize —
    // resize-triggered CD cascades into parent cs-card ResizeObserver loops
    if (!fromResize) {
      this.cd.markForCheck();
    }

    if (this.chartType === 'line') {
      if (this.datasets.length == 1) {
        // Calculate trend line to get values
        const trendLine = this.calculateTheilSenTrendLine(this.datasets[0].values);
        if (trendLine) {
          // Use the calculated current value (weight 7 days ago) - matching plan-update.ts
          this.currentValue = trendLine.currentValue;
          // Calculate weekly gain/loss (slope * 7 days) - matching plan-update.ts: weightSlope_kgwk = weightSlopeM * 7
          const weeklyChange = (trendLine.endValue - trendLine.startValue) / (this.datasets[0].values.length - 1) * 7;
          this.datasetsGains.push(roundToPrecision(weeklyChange, 0.1));
        } else {
          // Fallback to actual values if trend line can't be calculated
          const datasetValues = this.datasets[0].values.filter(v => v != null);
          this.currentValue = datasetValues.length > 0 ? datasetValues[datasetValues.length - 1] : 0;
          if (this.currentValue !== undefined && datasetValues.length > 0) {
            this.datasetsGains.push(roundToPrecision(this.currentValue - datasetValues[0], 0.1));
          }
        }
      }
    } else {
      for (let iDataset = 0; iDataset < this.datasets.length; iDataset++) {
        const dataset = this.datasets[iDataset];
        this.datasetsGains[iDataset] = dataset.values.length > 0 ? Math.abs(dataset.values.reduce((a, b) => a + b, 0)) : 0;
      }
    }
  }

  onSeeAllClick() {
    console.log('See all clicked');
    this.seeAllClick.emit();
  }

}
